// Bilder des Grafik-Leads im Kampf (Phase Kern, T21, T31, K38, K39): Manifest und Bilder aus public/art/ laden und
// scharf zeichnen. Der Ordner gehört dem Grafik-Lead; hier wird nur gelesen. Fehlt ein Bild oder passt eine ID nicht,
// zeichnet das Ufer weiter Kreise, und nichts bricht (Hinweise des Projekt-Leads in docs/kern.md).

export type ArtAnim = {
  row: number;
  frames: number;
  /** [Bild, Millisekunden] */
  sequence: [number, number][];
  loop: boolean;
  /** angriff: in diesem Bild trifft der Schlag */
  hitFrame?: number;
  /** wurf: in diesem Bild fliegt das Geschoss los, am Punkt wurf */
  releaseFrame?: number;
  /** doppelhieb (Version 3): Schritte der Folge, an denen ein Treffer sitzt */
  hitSteps?: number[];
  /** Version 4, weggehen: so gezeichnet, in diese Richtung geht die Figur (nicht spiegeln) */
  facing?: 'left' | 'right';
  /** weggehen: Schritte der Folge, in denen die Figur auf der Stelle steht; sonst geht sie mit speed (Feldeinheiten je Sekunde) */
  stillSteps?: number[];
  speed?: number;
  /** weggehen: so viele ms vor dem Ende ausblenden */
  fadeMs?: number;
};

/** Punkte im Bild; augen ist eine Liste mit einem Punkt je Auge (Version 3) */
export type ArtPoints = Record<string, [number, number] | [number, number][]>;

/** Alle Punkte eines Namens als Liste (augen hat zwei, die anderen einen) */
export function pointList(points: ArtPoints | undefined, name: string): [number, number][] {
  const p = points?.[name];
  if (!p) return [];
  return Array.isArray(p[0]) ? (p as [number, number][]) : [p as [number, number]];
}

/** Der erste Punkt eines Namens */
export const point1 = (points: ArtPoints | undefined, name: string): [number, number] | undefined => pointList(points, name)[0];

export type ArtSheet = {
  file: string;
  frameSize: [number, number];
  /** Fußpunkt, in jedem Bild derselbe */
  anchor: [number, number];
  facing?: 'left' | 'right';
  points?: ArtPoints;
  animations: Record<string, ArtAnim>;
};

export type ArtSprite = {
  id: string;
  kind: 'species' | 'enemy' | 'class' | 'marker' | 'projectile' | 'effect' | 'status' | 'trait' | 'ui' | 'crew';
  file: string;
  size?: [number, number];
  anchor?: [number, number];
  facing?: 'left' | 'right';
  points?: ArtPoints;
  sheet?: ArtSheet;
  /** G4: dieselben Bewegungen als Befallene, schon gespiegelt */
  infested?: ArtSheet;
  /** Streifen (Geschosse, Effekte, Zustände): Bilder nebeneinander */
  frameSize?: [number, number];
  frames?: number;
  ms?: number;
  center?: [number, number];
  color?: string;
  flight?: string;
  angles?: string[];
  /** Version 3: Kniffe und Anzeigen. [Bild, ms] statt ms je Bild */
  sequence?: [number, number][];
  /** kopf, kopfmitte, mitte, fuss, augen, mund, ruf; ziel:… heißt am Getroffenen */
  point?: string;
  /** vor, hinter, boden oder „nur auf der Figur“ */
  layer?: string;
  loop?: boolean;
  /** das letzte Bild bleibt, bis der Kniff endet */
  hold?: boolean;
  /** so viele ms vor dem Treffer starten */
  leadMs?: number;
  /** Abstand zwischen zwei Durchläufen bei Anzeigen, die nur ab und zu kommen */
  pauseMs?: number;
  /** Verschiebung in Bildpixeln, für Blick nach rechts */
  offset?: [number, number];
  /** ID in abilities.json */
  kniff?: string;
  /**
   * Version 8 (Erste Fassung): Bild eines Kniffs, den das Spiel bisher ohne Bild zeigte; der Grafik-Lead setzt es, bis der
   * Code die Bilder zeichnet. Seit Runde 7 angeschlossen und wie kniff behandelt
   */
  kniffNeu?: string;
  /** Version 8: Neigung in Grad (0 rechts, positiv nach unten), für Bilder, die es je Richtung gibt (Baum fällen) */
  angle?: number;
  /** Version 8: Ebene des ersten Bilds, solange es steht (Baum fällen: hinter dem Biber) */
  layerStand?: string;
  /** Version 8: Form der Ansage (area, ring), für Flächen und Ringe am Boden */
  shape?: string;
  /** Version 8: Fähigkeit, zu der ein Zeichen gehört (passiv-schlau, pfote-halten) */
  ability?: string;
  /** Version 8: welches Bild für welchen Zustand (passiv-schlau: ruhig, greift) */
  states?: Record<string, number>;
  /** ID in traits.json */
  trait?: string;
  /**
   * T37 (Fabi, 17.10.2026 nachts: „Wiedererkennung durch Differenzierbarkeit“): Farbvarianten einer Spezies. Aussehen 1
   * nimmt die erste, 2 die zweite usw.; swap tauscht Farben genau (#rrggbb → #rrggbb), in allen Bildern der Figur
   */
  palettes?: ArtPalette[];
  /** Version 4: Das Geschoss trägt die Farben dieser Spezies (Federpfeil des Eisvogels) */
  palettesFrom?: string;
  /** Version 4, leiste-muster: Maße und Farben der ruhigen Leiste */
  bar?: CalmBarSpec;
  /** Version 4, kleine Zustandszeichen: der Zustand (FrameFlag-Name oder wounded) und die Reihenfolge in der Reihe */
  flag?: string;
  order?: number;
  /** Version 4, bedroht-<rx>: Ringbreite im Feld, Reihen, Deckkraft; Version 6: Zeile, an der der hintere Teil endet */
  radius?: number;
  splitRow?: number;
  layerBack?: string;
  rows?: number;
  alpha?: number;
  /** Schlussrunde des Grafik-Leads, Licht je Biom: Deckkraft im Starkregen (nass) statt alpha */
  alphaNass?: number;
  /** Version 4, trotz-1 bis -3: ab diesem Anteil Leben und darunter, für diese Klasse */
  lifeBelow?: number;
  friendClass?: string;
  /** Version 4, zweites Häppchen: der Schein um die Figur, solange der Kniff wirkt (K38) */
  glow?: GlowSpec;
  /** ui: so viele Bildschirmpixel je Bildpixel (Flussleiste, Mauszeiger, Rahmen) */
  scale?: number;
  /** Übungsufer (Vorschlag): Rahmen und Zettel als Neunteiler, Ränder oben, rechts, unten, links in Bildpixeln */
  slice?: [number, number, number, number];
  /** Übungsufer (Vorschlag): Licht über dem Feld, so gemischt (multiply, screen, soft-light, overlay …) */
  blend?: string;
  /** Übungsufer (Vorschlag): Namen der Bilder eines Streifens, etwa ruhig, darueber, gedrueckt */
  frameNames?: string[];
};

/** Schein um die Figur: Farbe, Tönung der Figur (0,16 = 16 Prozent), Deckkraft pulsiert zwischen alpha, Breite in Bildpixeln */
export type GlowSpec = { color: string; tint: number; alpha: [number, number]; pulseMs: number; width: number };

/** Version 4: die ruhige Leiste in Bildpixeln (das Spiel zeichnet sie selbst) */
export type CalmBarSpec = {
  border: number;
  cornersCut?: boolean;
  lifeHeight: number;
  gapHeight: number;
  mutHeight: number;
  height: number;
  aboveHead: number;
  colors: {
    life: [string, string];
    lifeLow: [string, string];
    lowBelow: number;
    lost: [string, string];
    shield: [string, string];
    mut: string;
    mutFull: string;
    lifeEmpty: string;
    mutEmpty: string;
    border: string;
  };
};

/** Version 4 des Manifests: map ordnet Farben neue Farben zu (#alt → #neu); swap ist der ältere Name dafür */
export type ArtPalette = { id: string; name?: string; map?: Record<string, string>; swap?: Record<string, string> };

const DIR = 'art/';
let ppuValue = 4;
const byKey = new Map<string, ArtSprite>();
const kniffArt = new Set<string>();
const images = new Map<string, HTMLImageElement>();
const silhouettes = new Map<string, HTMLCanvasElement>();

/** Lädt das Manifest einmal; bis es da ist, bleiben die Kreise */
export function loadArt(): void {
  if (typeof fetch !== 'function') return;
  fetch(DIR + 'manifest.json')
    .then((r) => (r.ok ? r.json() : null))
    .then((m: ArtManifest | null) => {
      if (!m || !Array.isArray(m.sprites)) return;
      useManifest(m);
      // vorab laden, damit beim ersten Kampf nichts fehlt
      for (const sp of m.sprites) for (const f of [sp.file, sp.sheet?.file, sp.infested?.file]) if (f) image(f);
      // Version 4: Wer schon gezeichnet hat (die Flussleiste), zeichnet mit den Bildern neu
      if (typeof window !== 'undefined') window.dispatchEvent(new Event('art-loaded'));
    })
    .catch(() => {});
}

export type ArtManifest = { pixelsPerUnit?: number; sprites: ArtSprite[] };

/** Das Manifest übernehmen (beim Laden im Browser, in Tests direkt aus der Datei) */
export function useManifest(m: ArtManifest): void {
  ppuValue = m.pixelsPerUnit ?? 4;
  byKey.clear();
  kniffArt.clear();
  for (const sp of m.sprites) {
    byKey.set(`${sp.kind}:${sp.id}`, sp);
    // Runde 7: kniffNeu wie kniff (die Bilder der neuen Kniffe sind angeschlossen)
    const k = sp.kniff ?? sp.kniffNeu;
    if (k) kniffArt.add(k);
  }
}

/** Version 3: Hat der Grafik-Lead für diesen Kniff (ID in abilities.json) ein eigenes Bild gezeichnet? */
export const hasKniffArt = (kniff: string): boolean => kniffArt.has(kniff);

export const sprite = (kind: ArtSprite['kind'], id: string): ArtSprite | undefined => byKey.get(`${kind}:${id}`);

/** Ein Tempo als Schrift, etwa „0,5×“ */
export const speedLabel = (speed: number): string => `${String(speed).replace('.', ',')}×`;

/**
 * Runde 8 (Fabi: Tempo mit Zeichen): das Zeichen tempo-<speed> des Grafik-Leads als kleines Bild, Bild 1 (an) für das
 * gewählte Tempo, Bild 0 (ruhig) sonst; ohne Bild die Schrift
 */
export function speedIcon(speed: number, on: boolean): string {
  const sp = sprite('ui', 'tempo-' + String(speed).replace('.', ''));
  if (!sp) return speedLabel(speed);
  const [w, h] = sp.frameSize ?? [20, 20];
  const frame = sp.states?.[on ? 'an' : 'ruhig'] ?? (on ? 1 : 0);
  return `<i class="speed-art" style="width:${w}px;height:${h}px;background-image:url(art/${sp.file});background-size:${w * (sp.frames ?? 2)}px ${h}px;background-position:-${frame * w}px 0" aria-hidden="true"></i>`;
}

/** Alle Einträge des Manifests (Version 4: kleine Zeichen und Bögen sucht man nach Feldern statt nach Namen) */
export const sprites = (): ArtSprite[] => [...byKey.values()];

/** Das Bild, sobald es geladen ist; sonst null (dann zeichnet der Aufrufer den Kreis) */
export function image(file: string): HTMLImageElement | null {
  let img = images.get(file);
  if (!img) {
    img = new Image();
    img.src = DIR + file;
    images.set(file, img);
  }
  return img.complete && img.naturalWidth > 0 ? img : null;
}

// ---------- T37: Aussehen ----------

/**
 * T37: Platzhalter, solange der Grafik-Lead für eine Spezies keine (oder zu wenige) Farbvarianten gezeichnet hat:
 * Farbton, Sättigung und Helligkeit leicht verschoben; Umriss und Glanzlichter bleiben
 */
const PLACEHOLDER_LOOKS: { hue: number; sat: number; light: number }[] = [
  // dunkler und wärmer, heller und blasser, rötlich wie Kastanie, gräulich; wie Tiere sich in echt unterscheiden
  { hue: -6, sat: 1.05, light: 0.76 },
  { hue: 0, sat: 0.6, light: 1.24 },
  { hue: -16, sat: 1.3, light: 0.96 },
  { hue: 0, sat: 0.3, light: 1.02 },
];
const looks = new Map<string, HTMLCanvasElement>();

/** Wie das Bild für dieses Aussehen aussieht; 0 oder ohne Aussehen wie gezeichnet */
export function lookImage(file: string, look: number | undefined, sp: ArtSprite | undefined): CanvasImageSource | null {
  const img = image(file);
  if (!img || !look) return img;
  const key = `${file}|${look}`;
  const done = looks.get(key);
  if (done) return done;
  const c = document.createElement('canvas');
  c.width = img.naturalWidth;
  c.height = img.naturalHeight;
  const g = c.getContext('2d', { willReadFrequently: true })!;
  g.drawImage(img, 0, 0);
  const data = g.getImageData(0, 0, c.width, c.height);
  const pals = sp?.palettes ?? [];
  const pal = look ? pals[look - 1] : undefined;
  if (pal) recolor(data.data, pal.map ?? pal.swap ?? {});
  else if (look) tint(data.data, PLACEHOLDER_LOOKS[(look - pals.length - 1) % PLACEHOLDER_LOOKS.length]!);
  g.putImageData(data, 0, 0);
  looks.set(key, c);
  return c;
}

const hex = (r: number, g: number, b: number) => '#' + [r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('');

function recolor(px: Uint8ClampedArray, swap: Record<string, string>): void {
  const map = new Map(Object.entries(swap).map(([a, b]) => [a.toLowerCase(), b.toLowerCase()]));
  for (let i = 0; i < px.length; i += 4) {
    if (px[i + 3]! === 0) continue;
    const to = map.get(hex(px[i]!, px[i + 1]!, px[i + 2]!));
    if (!to || !/^#[0-9a-f]{6}$/.test(to)) continue;
    px[i] = parseInt(to.slice(1, 3), 16);
    px[i + 1] = parseInt(to.slice(3, 5), 16);
    px[i + 2] = parseInt(to.slice(5, 7), 16);
  }
}

function tint(px: Uint8ClampedArray, t: { hue: number; sat: number; light: number }): void {
  for (let i = 0; i < px.length; i += 4) {
    if (px[i + 3]! === 0) continue;
    const [h, s, l] = toHsl(px[i]! / 255, px[i + 1]! / 255, px[i + 2]! / 255);
    // Umriss (sehr dunkel) und Glanzlichter (fast weiß) bleiben, sonst verschwimmt die Figur
    if (l < 0.14 || (l > 0.88 && s < 0.25)) continue;
    const [r, g, b] = fromHsl((h + t.hue / 360 + 1) % 1, Math.min(1, s * t.sat), Math.min(0.92, l * t.light));
    px[i] = Math.round(r * 255);
    px[i + 1] = Math.round(g * 255);
    px[i + 2] = Math.round(b * 255);
  }
}

function toHsl(r: number, g: number, b: number): [number, number, number] {
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  const h = max === r ? (g - b) / d + (g < b ? 6 : 0) : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
  return [h / 6, s, l];
}

function fromHsl(h: number, s: number, l: number): [number, number, number] {
  if (s === 0) return [l, l, l];
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const ch = (t: number) => {
    const x = (t + 1) % 1;
    return x < 1 / 6 ? p + (q - p) * 6 * x : x < 1 / 2 ? q : x < 2 / 3 ? p + (q - p) * (2 / 3 - x) * 6 : p;
  };
  return [ch(h + 1 / 3), ch(h), ch(h - 1 / 3)];
}

/** T30: weiße Silhouette eines Bildes für das kurze Aufblitzen bei einem Treffer */
export const whiteOf = (file: string): HTMLCanvasElement | null => silhouetteOf(file, '#fffaf0');

/** Das Bild in einer Farbe, etwa grau für benommen */
export function silhouetteOf(file: string, color: string): HTMLCanvasElement | null {
  const key = `${file}|${color}`;
  const done = silhouettes.get(key);
  if (done) return done;
  const img = image(file);
  if (!img) return null;
  const c = document.createElement('canvas');
  c.width = img.naturalWidth;
  c.height = img.naturalHeight;
  const g = c.getContext('2d')!;
  g.drawImage(img, 0, 0);
  g.globalCompositeOperation = 'source-in';
  g.fillStyle = color;
  g.fillRect(0, 0, c.width, c.height);
  silhouettes.set(key, c);
  return c;
}

// ---------- Figuren oder Kreise (Taste G) ----------

const STORE = 'fff.figuren';
let figuresOn = (() => {
  try {
    return localStorage.getItem(STORE) !== 'aus';
  } catch {
    return true;
  }
})();

/** Ob die Figuren gezeigt werden sollen; ohne Bilder bleiben trotzdem Kreise */
export const figuresWanted = (): boolean => figuresOn;

export function toggleFigures(): boolean {
  figuresOn = !figuresOn;
  try {
    localStorage.setItem(STORE, figuresOn ? 'an' : 'aus');
  } catch {
    // ohne Speicher gilt die Wahl nur bis zum Neuladen
  }
  return figuresOn;
}

/** Bildpixel je Feldeinheit (manifest.json) */
export const pixelsPerUnit = (): number => ppuValue;

/**
 * Gerätepixel je Bildpixel: ganzzahlig, damit die Pixel scharf bleiben (kern.md). Gerundet auf die nächste ganze Zahl;
 * würde die Figur dabei kleiner als vier Fünftel ihrer gedachten Größe, eine Stufe größer, damit man sie gut sieht.
 */
export function pixelScale(s: number): number {
  const k = Math.max(1, Math.round(s / ppuValue));
  return (k * ppuValue) / s < 0.8 ? k + 1 : k;
}

/**
 * Ein Bild aus einem Sheet oder Streifen zeichnen. (x, y) ist in Gerätepixeln der Punkt `pin` im Bild,
 * etwa der Fußpunkt; flip spiegelt waagrecht um diesen Punkt.
 */
export function blit(
  ctx: CanvasRenderingContext2D,
  src: CanvasImageSource,
  sx: number,
  sy: number,
  sw: number,
  sh: number,
  x: number,
  y: number,
  pin: [number, number],
  k: number,
  flip = false,
): void {
  ctx.imageSmoothingEnabled = false;
  const px = flip ? sw - 1 - pin[0] : pin[0];
  const dx = Math.round(x - (px + 0.5) * k);
  const dy = Math.round(y - (pin[1] + 1) * k);
  if (!flip) {
    ctx.drawImage(src, sx, sy, sw, sh, dx, dy, sw * k, sh * k);
    return;
  }
  ctx.save();
  ctx.translate(dx + sw * k, dy);
  ctx.scale(-1, 1);
  ctx.drawImage(src, sx, sy, sw, sh, 0, 0, sw * k, sh * k);
  ctx.restore();
}

/** Ein Bild aus einem Streifen (Geschoss, Effekt, Zustand) mittig auf (x, y) zeichnen; false, wenn es fehlt */
export function drawStrip(
  ctx: CanvasRenderingContext2D,
  kind: ArtSprite['kind'],
  id: string,
  frame: number,
  x: number,
  y: number,
  k: number,
  opts: { flip?: boolean; alpha?: number; /** T37: das Bild in einem anderen Aussehen */ img?: CanvasImageSource | null } = {},
): boolean {
  const sp = sprite(kind, id);
  const base = sp ? image(sp.file) : null;
  const img = sp && base ? (opts.img ?? base) : null;
  if (!sp || !img || !base) return false;
  const [fw, fh] = sp.frameSize ?? sp.size ?? [base.naturalWidth, base.naturalHeight];
  const n = Math.max(1, sp.frames ?? 1);
  const f = ((frame % n) + n) % n;
  const c = sp.center ?? [Math.floor(fw / 2), Math.floor(fh / 2)];
  ctx.save();
  if (opts.alpha !== undefined) ctx.globalAlpha *= opts.alpha;
  blit(ctx, img, f * fw, 0, fw, fh, x, y, [c[0], c[1] - 0.5], k, opts.flip);
  ctx.restore();
  return true;
}

/** Welches Bild eines Streifens zur Zeit t (Sekunden) dran ist */
export function stripFrame(kind: ArtSprite['kind'], id: string, t: number): number {
  const sp = sprite(kind, id);
  if (!sp || !sp.ms || !sp.frames) return 0;
  return Math.floor((t * 1000) / sp.ms) % sp.frames;
}

/** Dauer eines Streifens in Sekunden (einmal durch) */
export function stripSeconds(kind: ArtSprite['kind'], id: string): number {
  const sp = sprite(kind, id);
  return sp?.ms && sp.frames ? (sp.ms * sp.frames) / 1000 : 0.3;
}

/** Welches Bild einer Bewegung nach `at` Sekunden dran ist; bei Schleifen läuft es weiter, sonst bleibt das letzte */
export function animFrame(a: ArtAnim, at: number): number {
  const total = a.sequence.reduce((sum, [, ms]) => sum + ms, 0) / 1000;
  if (total <= 0) return a.sequence[0]?.[0] ?? 0;
  let t = a.loop ? ((at % total) + total) % total : Math.min(Math.max(0, at), total - 1e-6);
  for (const [f, ms] of a.sequence) {
    t -= ms / 1000;
    // genau auf der Grenze zählt schon das nächste Bild, damit das Trefferbild auf den Treffer fällt
    if (t < -1e-9) return f;
  }
  return a.sequence[a.sequence.length - 1]![0];
}

/** Sekunden vom Beginn einer Bewegung bis zum Beginn des Bildes `frame` (für hitFrame und releaseFrame) */
export function secondsUntilFrame(a: ArtAnim, frame: number): number {
  let ms = 0;
  for (const [f, d] of a.sequence) {
    if (f === frame) return ms / 1000;
    ms += d;
  }
  return 0;
}

export const animSeconds = (a: ArtAnim): number => a.sequence.reduce((sum, [, ms]) => sum + ms, 0) / 1000;

/** Sekunden vom Beginn einer Bewegung bis zum Schritt `step` der Folge (doppelhieb: hitSteps) */
export function secondsUntilStep(a: ArtAnim, step: number): number {
  return a.sequence.slice(0, step).reduce((sum, [, ms]) => sum + ms, 0) / 1000;
}

/** Folge eines Streifens (Version 3): sequence, sonst ms je Bild */
function stripSequence(sp: ArtSprite): [number, number][] {
  if (sp.sequence?.length) return sp.sequence;
  const n = Math.max(1, sp.frames ?? 1);
  return Array.from({ length: n }, (_, i) => [i, sp.ms ?? 100] as [number, number]);
}

/** Dauer eines Kniff-Bilds oder einer Anzeige, einmal durch, in Sekunden */
export function fxSeconds(sp: ArtSprite | undefined): number {
  if (!sp) return 0;
  return stripSequence(sp).reduce((sum, [, ms]) => sum + ms, 0) / 1000;
}

/**
 * Welches Bild eines Kniff-Bilds oder einer Anzeige nach `age` Sekunden dran ist (Version 3): Schleifen laufen weiter,
 * hold behält das letzte Bild, sonst ist es nach einem Durchlauf weg (null)
 */
export function fxFrame(sp: ArtSprite, age: number): number | null {
  if (age < 0) return null;
  const seq = stripSequence(sp);
  const total = seq.reduce((sum, [, ms]) => sum + ms, 0) / 1000;
  if (total <= 0) return seq[0]?.[0] ?? 0;
  let t = age;
  if (sp.loop) t = age % total;
  else if (age >= total) return sp.hold ? seq[seq.length - 1]![0] : null;
  for (const [f, ms] of seq) {
    t -= ms / 1000;
    if (t < -1e-9) return f;
  }
  return seq[seq.length - 1]![0];
}
